<template>
  <div>

    <accounting-links v-if="isReportPage"></accounting-links>

    <div class="box" :class="{ 'py-1 px-1': compact }">
      <div class="flex flex-wrap items-center" v-if="showActions">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getCreditNotes" />
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <button
            v-if="hasPermission('credit-note.export')"
            type="button"
            class="btn btn-green text-sm px-3 flex items-center mb-1"
            :disabled="!exportFile.creditNote.enabled"
            @click="exportCreditNotes"
          >
            <i class="fas fa-spinner fa-spin mr-2" v-if="!exportFile.creditNote.enabled"></i>
            <span>Export</span>
          </button>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getCreditNotes">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>

      </div>


      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <credit-note-filters
            :filters.sync="filters"
            @updated="getCreditNotes"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>
        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">

          <p class="text-lg text-center my-12" v-if="!hasCreditNotes">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th v-if="!compact">#</th>
                  <th>Contact</th>
                  <th v-if="!compact">Invoice</th>
                  <th>Type</th>
                  <th v-if="allPriceColumns">Discount</th>
                  <th v-if="allPriceColumns">Tax</th>
                  <th v-if="allPriceColumns">Net</th>
                  <th>Total</th>
                  <th>Created at</th>
                  <th title="Settlement date">Settl. date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(creditNote, i) in creditNotes" :key="i">
                  <td class="font-bold" v-if="!compact">{{ creditNote.id }}</td>
                  <td>
                    <a v-if="!customerID" class="text-link" @click.stop="$router.push('/customer/details/' + creditNote.contact.id)">
                      <span v-if="!compact">
                        #{{ creditNote.contact.id }}
                      </span>
                      {{ creditNote.contact.first_name }}
                      {{ creditNote.contact.last_name }}
                      <span v-if="!compact && creditNote.contact.company_name">
                        ({{ creditNote.contact.company_name }})
                      </span>
                    </a>
                  </td>
                  <td v-if="!compact">
                    <div
                      v-if="creditNote.invoice && creditNote.invoice.id"
                      class="text-link"
                      @click.stop="$router.push('/invoice/preview/' + creditNote.invoice.id)"
                    >
                      <p>Invoice #{{ creditNote.invoice.id }}</p>
                    </div>
                    <div v-else> - </div>
                  </td>
                  <td>
                    <p
                      class="font-bold text-xs text-white py-1 px-1 rounded text-center uppercase"
                      :class="creditNote.type === 'purchases' ? 'bg-red-500' : 'bg-green-500'"
                    >{{ creditNote.type }}</p>
                  </td>
                  <td v-if="allPriceColumns">{{ $formatMoney(creditNote.total.discount) }}</td>
                  <td v-if="allPriceColumns">{{ $formatMoney(creditNote.total.tax) }}</td>
                  <td v-if="allPriceColumns">{{ $formatMoney(creditNote.total.sub - creditNote.total.discount) }}</td>
                  <td>
                    <span v-if="allPriceColumns">{{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax) }}</span>
                    <span v-else
                      class="font-bold"
                      v-tooltip="{ content: `
                        <strong>Discount:</strong> ${$formatMoney(creditNote.total.discount)}
                        <strong>Tax:</strong> ${$formatMoney(creditNote.total.tax)}
                      ` }"
                    >
                      {{ $formatMoney(creditNote.total.sub - creditNote.total.discount + creditNote.total.tax) }}
                    </span>
                  </td>
                  <td class="text-xs">{{ $moment(creditNote.created_at).format(config.dateTimeFormats.display) }}</td>
                  <td>
                    <span class="text-green-500 font-bold" v-if="creditNote.settlement_date">
                      {{ $moment(creditNote.settlement_date).format(config.dateTimeFormats.display) }}
                    </span>
                    <span class="text-red-500 leading-loose px-1 rounded" v-else>not settled</span>
                  </td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/credit-note/preview/' + creditNote.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        v-if="!creditNote.settlement_date && hasPermission('credit-note.edit')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/credit-note/edit/' + creditNote.id)"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        v-if="!creditNote.settlement_date && creditNote.items.length <= 0 && hasPermission('credit-note.delete')"
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteCreditNote(creditNote.id)"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasCreditNotes && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const CreditNoteFilters = resolve => require(['@/components/filters/Filters'], resolve)
const AccountingLinks = resolve => require(['@/components/pages/reports/AccountingLinks'], resolve)
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)

export default {
  name: 'credit-note-list',
  props: {
    customerID: {},
    showActions: {
      type: Boolean,
      default: true,
    },
    type: String,
    compact: Boolean,
  },
  components: {
    FilteringToggleButton,
    CreditNoteFilters,
    AccountingLinks,
    ListSearch,
    PaginationComponent,
  },
  data() {
    return {
      isReportPage: false,
      allPriceColumns: false,
      creditNotes: [],
      jobTypes: [],
      searchInput: '',
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        type: {
          key: 'type',
          name: 'Type',
          active: true,
          options: [
            {value: 'sales', class: 'btn-green'},
            {value: 'purchases', class: 'btn-red'},
          ],
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
        settled: {
          key: 'settled',
          name: 'Settled',
          active: true,
          options: [
            {name: 'Settled', value: true, class: 'btn-green'},
            {name: 'Not Settled', value: false, class: 'btn-red'},
          ],
          value: null,
          children: [
            {
              key: 'settlement_date',
              name: 'Settlement date',
              active: true,
              showWhenParentValue: true,
              flatpickr: {
                mode: 'range',
                static: false,
              },
              value: null,
            }
          ]
        },
      },
      query: {
        search: '',
      },
      exportFile: {
        creditNote: {
          enabled: true,
        }
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getCreditNotes()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getCreditNotes()
      }
    },
    creditNotes() {
      for (let i in this.creditNotes) {
        const total = {
          sub: 0,
          discount: 0,
          tax: 0,
        }
        this.creditNotes[i].items.map(v => {
          v.total = {
            raw: v.value * v.quantity,
          }
          v.total.discount = v.total.raw * v.discount / 100
          v.total.tax = (v.total.raw - v.total.discount) * v.tax / 100
          v.total.all = v.total.raw - v.total.discount + v.total.tax
          total.sub += v.total.raw
          total.discount += v.total.discount
          total.tax += v.total.tax
        })
        this.creditNotes[i].total = total
        // this.$set(this.creditNotes, i, Object.assign(this.creditNotes[i],total)
      }
    },
  },
  computed: {
    hasCreditNotes() {
      return Object.entries(this.creditNotes).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  created() {
    this.setRouteFilters()
  },
  mounted() {
    if (this.$route.name.split('.')[0] === 'report') {
      this.isReportPage = true
      this.allPriceColumns = true
    }
    this.getCreditNotes()
  },
  methods: {
    getFilterValues() {
      return this.$getFilterValues(this.filters)
    },
    _setRouteFilters(routeFilters) {
      for (let name in routeFilters) {
        for (let i in this.filters) {
          if (this.filters[i].key === name) {
            Object.assign(this.filters[i], routeFilters[name])
          }
        }
        if (routeFilters.children) {
          this._setRouteFilters(routeFilters.children)
        }
      }
    },
    setRouteFilters() {
      if (!this.$route.meta.filters) {
        return
      }
      let routeFilters = this.$route.meta.filters
      this._setRouteFilters(routeFilters)
    },
    exportCreditNotes() {
      this.exportFile.creditNote.enabled = false

      let filters = this.getFilterValues()
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }
      if (filters.settlement_date) {
        let settlementDate = this.$getRangeDates(filters.settlement_date, this.config)
        filters.settlementDateFrom = settlementDate.from
        filters.settlementDateTo = settlementDate.to
      }

      axios.get('credit-notes/export', {
        params: {
          search: this.searchInput,
          filters,
        },
        responseType: 'arraybuffer'
      })
        .then((response) => {
          this.$downloadFile(response)
          this.exportFile.creditNote.enabled = true
        })
    },
    deleteCreditNote(creditNoteID) {
      this.$swal.fire({
        title: 'Delete credit note',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('credit-notes/' + creditNoteID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getCreditNotes()
              })
          }
        })
    },
    getJobTypes() {
      axios.get('job-types')
        .then((response) => {
          this.jobTypes = response.data
          for (let i in this.creditNotes) {
            const inv = this.creditNotes[i]
            if (inv.job) {
              for (let j in this.jobTypes) {
                if (this.jobTypes[j].id === inv.job.job_type_id) {
                  this.$set(inv.job, 'type', this.jobTypes[j])
                }
              }
            }
          }
        })
    },
    getCreditNotes() {
      let filters = this.getFilterValues()
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }
      if (filters.settlement_date) {
        let settlementDate = this.$getRangeDates(filters.settlement_date, this.config)
        filters.settlementDateFrom = settlementDate.from
        filters.settlementDateTo = settlementDate.to
      }

      axios.get('credit-notes', {params: {
        page: this.pagination.currentPage,
        search: this.searchInput,
        filters,
      }})
        .then(response => {
          if (response.data) {
            this.getJobTypes()

            const res = response.data
            this.pagination.perPage = res.per_page
            this.pagination.totalRows = res.total
            this.creditNotes = Object.assign({}, res.data)


            this.query.search = this.searchInput
          }
        })
    }
  }
}
</script>
